import React, { useState, useEffect, useRef } from "react";
import { ArrowRight, Plus, Minus } from "lucide-react";
import Layout from "../components/ui/Layout";
import { Link } from "react-router-dom";
import ProgressBar from "../components/ui/ProgressBar";
import ScrollToTopButton from "../components/ui/ScrollToTopButton";
import { ICArticleBody, ICH2, ICFigure } from "../components/ui/IntranetArticle";
import "./IntranetConnections.css";
import CaseOpen from "../components/ui/CaseOpen";
import { IC_DESIGN_SYSTEM_COVER } from "../data/caseStudyMedia";

const getApproachSteps = (onZoom) => [
  {
    title: "Establish team alignment",
    description: (
      <>
        <p>
          <strong>Goal:</strong> Define vision, scope, and alignment between product, design, and development.
        </p>
        <p>
          <strong>Actions:</strong> Stakeholder meetings on goals and priorities; cross-functional collaboration between
          design and engineering.
        </p>
        <p>
          <strong>Why it matters:</strong> Less backtracking, business goals drive the system, realistic scope for v1.
        </p>
      </>
    ),
  },
  {
    title: "Component & UX defect audit",
    description: (
      <>
        <p>
          <strong>Goal:</strong> Find inconsistencies, orphaned custom components, and MudBlazor-specific gaps.
        </p>
        <p>
          <strong>Actions:</strong> AI-assisted diff between MudBlazor and the MUI kit; team audit; backlog UI defects;
          categorize by source and status.
        </p>
        <p>
          <strong>Why it matters:</strong> Organized inventory before migration; clarity on what earns a place in the
          system.
        </p>
      </>
    ),
  },
  {
    title: "Start from proven templates",
    description: (
      <>
        <p>
          <strong>Goal:</strong> Move fast with frameworks that fit IC 3.0 goals, brand, and team capacity.
        </p>
        <p>
          <strong>Actions:</strong> Researched UI libraries; picked a structure that matched business and resourcing.
        </p>
        <img
          src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741838025/Screenshot_2025-03-12_at_8.53.37_PM_aylknx.png"
          alt="Design system structure in Figma"
          onClick={() =>
            onZoom("https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741838025/Screenshot_2025-03-12_at_8.53.37_PM_aylknx.png")
          }
        />
      </>
    ),
  },
  {
    title: "Foundations & theming variables",
    description: (
      <>
        <p>
          <strong>Goal:</strong> Align foundations with MudBlazor; support multiple preset themes without manual
          recoloring.
        </p>
        <p>
          <strong>Actions:</strong> Adjusted local colors; added variables and tokens for theming.
        </p>
        <img
          src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741840312/Screenshot_2025-03-12_at_9.31.47_PM_kwhcoy.png"
          alt="Color and token setup"
          onClick={() =>
            onZoom("https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741840312/Screenshot_2025-03-12_at_9.31.47_PM_kwhcoy.png")
          }
        />
      </>
    ),
  },
  {
    title: "Migrate adopted MUI components",
    description: (
      <>
        <p>
          <strong>Goal:</strong> A clean, useful component base mapped to dev behavior.
        </p>
        <ul>
          <li>Compared MUI kit vs MudBlazor (including mobile)</li>
          <li>Mapped behaviors and tokens for design to dev parity</li>
          <li>Migrated selected components; adjusted properties where needed</li>
        </ul>
      </>
    ),
  },
  {
    title: "MudBlazor & IC custom components",
    description: (
      <>
        <p>
          <strong>Goal:</strong> Cover high-impact patterns and platform-specific UI in one library.
        </p>
        <ul>
          <li>Reusable components for common patterns</li>
          <li>MudBlazor-specific pieces added</li>
          <li>Rare components queued for later</li>
        </ul>
        <img
          src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741839919/InDragAreaFields_vyvzvd.png"
          alt="Custom IC components in the library"
          onClick={() =>
            onZoom("https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741839919/InDragAreaFields_vyvzvd.png")
          }
        />
      </>
    ),
  },
  {
    title: "Documentation & team education",
    description: (
      <>
        <p>
          <strong>Goal:</strong> Document vision, branding, and usage; sync with the team to refine and adopt.
        </p>
        <img
          src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741840691/Group_42_irpczl.png"
          alt="Design system documentation"
          onClick={() =>
            onZoom("https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741840691/Group_42_irpczl.png")
          }
        />
      </>
    ),
  },
];

const DesignSystemCaseStudy = () => {
  const caseRef = useRef(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleZoom = (imageSrc) => {
    setZoomedImage((prev) => (prev === imageSrc ? null : imageSrc));
  };

  const approachSteps = getApproachSteps(toggleZoom);

  useEffect(() => {
    document.body.style.overflow = zoomedImage ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [zoomedImage]);

  const toggleExpand = (index) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
    if (expandedIndex !== index) {
      setTimeout(() => {
        const element = document.getElementById(`accordion-${index}`);
        if (!element) return;
        const offset = element.getBoundingClientRect().top + window.pageYOffset - 80;
        window.scrollTo({ top: offset, behavior: "smooth" });
      }, 100);
    }
  };

  return (
    <Layout>
      {zoomedImage && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black z-50"
          onClick={() => setZoomedImage(null)}
        >
          <img
            src={zoomedImage}
            alt="Zoomed view"
            className="w-auto h-auto max-w-full max-h-full object-contain cursor-zoom-out"
          />
        </div>
      )}

      <a
        href="#design-system"
        className="sr-only focus:not-sr-only focus:fixed focus:top-0 focus:left-0 focus:bg-white focus:p-4 focus:z-50 focus:text-black focus:underline"
      >
        Skip to Content
      </a>

      {!zoomedImage && <ProgressBar />}
      <ScrollToTopButton />

      <div className="intranet-case" ref={caseRef}>
        <CaseOpen
          src={IC_DESIGN_SYSTEM_COVER}
          alt="IC 3.0 design system overview"
          eyebrow="Case study · IC 3.0 · Design system"
          title="Building a scalable design system for a legacy B2B platform"
          dek="From fragmented UI kits and 10+ defects per page to a themeable system: 10 → 3 UI defects per page and ~150 minutes saved per sprint in sync meetings."
          overviewId="design-system"
          meta={[
            { label: "Role", value: "Solo product design" },
            { label: "Timeline", value: "3 months, 2024" },
            { label: "Team", value: "Engineering" },
            { label: "Stack", value: "Figma · MudBlazor" },
          ]}
        />

        <section className="ic-section ic-section--divider">
          <ICH2>Problems</ICH2>
          <ICArticleBody>
            <p>
              When I joined IC 3.0, I inherited fragmented Figma kits and a live product that often didn&apos;t match
              design. MudBlazor was in stack, but ownership and customization were unclear.
            </p>
            <p>We averaged 10+ UI defects per page, endless QA loops for visual and interaction bugs.</p>
            <p>
              <a
                className="ic-figma-link"
                href="https://www.figma.com/community/file/1482307783595002655"
                target="_blank"
                rel="noreferrer"
              >
                Play with components in Figma
                <ArrowRight size={16} className="inline ml-1 align-text-bottom" aria-hidden />
              </a>
            </p>
          </ICArticleBody>
          <div className="ic-figure-grid ic-figure-grid--2 ic-focus-block">
            <figure className="ic-figure">
              <img
                src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741823247/Widget_Page_Edit-_Blank_qi8kbs.png"
                alt="Design inconsistencies"
                onClick={() =>
                  toggleZoom(
                    "https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741823247/Widget_Page_Edit-_Blank_qi8kbs.png"
                  )
                }
              />
              <figcaption>Design: inconsistent patterns</figcaption>
            </figure>
            <figure className="ic-figure">
              <img
                src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741642452/UI_Debts_ugoa6v.png"
                alt="Development UI debt"
                onClick={() =>
                  toggleZoom("https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741642452/UI_Debts_ugoa6v.png")
                }
              />
              <figcaption>Development: UI debt in production</figcaption>
            </figure>
          </div>
        </section>

        <section className="ic-section">
          <ICArticleBody>
            <p>
              Roughly 80% of custom components weren&apos;t integrated into the system or maintained in Figma; updates
              rippled as one-off fixes across pages and versions.
            </p>
          </ICArticleBody>
          <ICFigure
            src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741833725/Colour_styles_1_buzv9s.png"
            alt="Unmaintained color styles"
            caption="Color styles scattered outside the shared library."
            onZoom={toggleZoom}
          />
        </section>

        <section className="ic-section">
          <ICArticleBody>
            <p>
              Theming meant detaching styles and recoloring by hand across four preset themes (with more planned), unsustainable for designers and impossible to hand off cleanly to dev.
            </p>
          </ICArticleBody>
          <ICFigure
            src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741834351/Item_editing_default_luv7xn.png"
            alt="Manual theming workflow"
            caption="Before tokens: manual theme work on every surface."
            onZoom={toggleZoom}
          />
        </section>

        <section className="ic-section">
          <ICArticleBody>
            <blockquote className="ic-quote ic-quote--center">
              How do we quickly build a design system that is scalable, themeable, and sustainable for this project?
            </blockquote>
          </ICArticleBody>
        </section>

        <section className="ic-section ic-section--divider">
          <ICH2>Approach</ICH2>
          <ICArticleBody>
            <p>Design-led, built with the team, accelerated with AI where it saved audit time, not where it replaced
              judgment.</p>
          </ICArticleBody>
          <div className="ic-accordion ic-focus-block">
            {approachSteps.map((item, index) => (
              <div key={item.title} className="ic-accordion__item" id={`accordion-${index}`}>
                <button
                  type="button"
                  className="ic-accordion__trigger"
                  onClick={() => toggleExpand(index)}
                  aria-expanded={expandedIndex === index}
                >
                  <span>
                    {index + 1}. {item.title}
                  </span>
                  {expandedIndex === index ? <Minus size={18} aria-hidden /> : <Plus size={18} aria-hidden />}
                </button>
                {expandedIndex === index ? <div className="ic-accordion__panel">{item.description}</div> : null}
              </div>
            ))}
          </div>
        </section>

        <section className="ic-section ic-section--divider">
          <ICH2>Key metrics</ICH2>
          <div className="ic-stats ic-focus-block">
            <div>
              <span className="ic-stats__value">10 → 3</span>
              <span className="ic-stats__label">Average UI defects per page</span>
            </div>
            <div>
              <span className="ic-stats__value">150 min</span>
              <span className="ic-stats__label">Saved per sprint in product to dev sync</span>
            </div>
          </div>
        </section>

        <section className="ic-section">
          <ICH2>Results</ICH2>
          <ICArticleBody>
            <p>Design and development moved closer, same components, same tokens, same nav patterns.</p>
          </ICArticleBody>
          <p className="ic-kicker ic-focus-block">Navigation: before &amp; after</p>
          <div className="ic-figure-grid ic-figure-grid--2 ic-focus-block">
            <figure className="ic-figure">
              <img
                src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741679354/Nav_-_1440px_sfnxmo.png"
                alt="Before: design"
                onClick={() =>
                  toggleZoom("https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741679354/Nav_-_1440px_sfnxmo.png")
                }
              />
              <figcaption>Before · design</figcaption>
            </figure>
            <figure className="ic-figure">
              <img
                src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741679628/Overflowing_solution_2_oqupvv.png"
                alt="After: design"
                onClick={() =>
                  toggleZoom(
                    "https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741679628/Overflowing_solution_2_oqupvv.png"
                  )
                }
              />
              <figcaption>After · design</figcaption>
            </figure>
            <figure className="ic-figure">
              <img
                src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741679360/Group_1000003413_febz5n.png"
                alt="Before: development"
                onClick={() =>
                  toggleZoom("https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741679360/Group_1000003413_febz5n.png")
                }
              />
              <figcaption>Before · dev</figcaption>
            </figure>
            <figure className="ic-figure">
              <img
                src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741679362/Group_1000003414_iffxhh.png"
                alt="After: development"
                onClick={() =>
                  toggleZoom("https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741679362/Group_1000003414_iffxhh.png")
                }
              />
              <figcaption>After · dev</figcaption>
            </figure>
          </div>
        </section>

        <section className="ic-section ic-section--divider">
          <ICArticleBody className="ic-article__body ic-article__footer" focus={false}>
            <Link to="/IntranetConnections" className="ic-nav-link">
              ← Intranet Connections
            </Link>
            <Link to="/HSPPlanet" className="ic-nav-link">
              HSP Planet →
            </Link>
          </ICArticleBody>
        </section>
      </div>
    </Layout>
  );
};

export default DesignSystemCaseStudy;
